import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download, Lock, Search, Loader2, Heart, Crown, Tv,
  X, Film, Star, HardDrive, Image as ImageIcon,
  Info, Clock, Monitor, Volume2, Subtitles, Sparkles, Activity,
} from "lucide-react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MediaToggle from "@/components/MediaToggle";
import Lightbox from "@/components/Lightbox";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// -- Helpers ----------------------------------------------------------------

const TMDB_W500 = "https://image.tmdb.org/t/p/w500";

function humanSize(bytes) {
  if (!bytes) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
}

function buildLabel(t) {
  if (t.media_type === "tv") {
    const seasons = (t.seasons || []).filter((s) => Number.isInteger(s));
    if (seasons.length > 1) {
      return `${t.file_count} eps · S${Math.min(...seasons)}–S${Math.max(...seasons)}`;
    }
    if (seasons.length === 1) return `${t.file_count} eps · S${seasons[0]}`;
    return `${t.file_count} eps`;
  }
  if (t.file_count > 1) return `${t.file_count} versions`;
  return (t.qualities || []).filter(Boolean)[0]?.toUpperCase() || "Movie";
}

const PAGE_SIZE = 60;

// -- Main page --------------------------------------------------------------

export default function DownloadsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mediaType, setMediaType] = useState("movie");
  const [quality, setQuality] = useState("");   // "", "720p", "1080p", "2160p"
  const [hdrOnly, setHdrOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sub, setSub] = useState(null);
  const [active, setActive] = useState(null); // selected title for modal

  const isPremium = (sub?.tier || user?.tier || "none") === "premium";
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadSub = async () => {
    try { setSub((await api.get("/me/subscription")).data); } catch { /* */ }
  };

  const loadList = async (mt = mediaType, q = submittedSearch, p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/downloads/titles", {
        params: {
          media_type: mt,
          search: q,
          quality: quality || undefined,
          hdr: hdrOnly || undefined,
          limit: PAGE_SIZE,
          skip: (p - 1) * PAGE_SIZE,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load downloads");
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [mediaType, submittedSearch, quality, hdrOnly]);

  useEffect(() => { loadSub(); }, []);
  useEffect(() => {
    if (isPremium) loadList(mediaType, submittedSearch, page);
    else setLoading(false);
  }, [isPremium, mediaType, submittedSearch, page, quality, hdrOnly]);

  const submitSearch = () => {
    setSubmittedSearch(search.trim());
  };

  const goPage = (p) => {
    setPage(p);
    // Scroll the main scroll container to top
    document.querySelector("main")?.parentElement?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />

        <main className="flex-1 px-4 md:px-8 pt-6 pb-12">
          <PageHero isPremium={isPremium} sub={sub} />

          {!user || !isPremium ? (
            <LockedHero tier={user?.tier} />
          ) : (
            <>
              <MediaToggle value={mediaType} onChange={setMediaType} />

              <QualityChips
                quality={quality}
                hdrOnly={hdrOnly}
                onQuality={setQuality}
                onHdr={setHdrOnly}
              />

              <div className="flex flex-col sm:flex-row gap-2 mb-5 max-w-3xl mx-auto">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tt-text-muted)]" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); }}
                    placeholder={`Search the cloud library for ${mediaType === "tv" ? "shows" : "movies"}…`}
                    data-testid="downloads-search"
                    className="w-full pl-9 pr-3 py-2.5 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl text-sm outline-none focus:border-[var(--tt-primary)]"
                  />
                </div>
                <button
                  onClick={submitSearch}
                  data-testid="downloads-search-btn"
                  className="px-5 py-2.5 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-sm font-bold"
                >Search</button>
              </div>

              {loading ? (
                <SkeletonGrid />
              ) : items.length === 0 ? (
                <div className="py-16 text-center text-[var(--tt-text-faint)]">
                  No matches. Try a different search.
                </div>
              ) : (
                <>
                  <p className="text-[11px] text-[var(--tt-text-muted)] mb-4 font-mono-tt text-center" data-testid="downloads-results-meta">
                    Page {page} of {totalPages} · {total.toLocaleString()} {mediaType === "tv" ? "shows" : "movies"}
                  </p>
                  <div
                    data-testid="downloads-grid"
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
                  >
                    {items.map((t) => (
                      <DownloadTitleCard
                        key={`${t.media_type}-${t.tmdb_id}`}
                        title={t}
                        onOpen={() => setActive(t)}
                      />
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <Pagination page={page} totalPages={totalPages} onChange={goPage} />
                  )}
                </>
              )}
            </>
          )}
        </main>

        <Footer />
      </div>

      <TitleDownloadModal
        title={active}
        onClose={() => setActive(null)}
        onAfterDownload={loadSub}
      />
    </div>
  );
}

// -- Page hero & paywall ----------------------------------------------------

function PageHero({ isPremium, sub }) {
  return (
    <div className="flex items-center gap-3 mb-6 max-w-7xl mx-auto w-full">
      <div className="w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 flex items-center justify-center">
        <Download className="w-6 h-6 text-[var(--tt-primary)]" />
      </div>
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Direct downloads</h1>
        <p className="text-sm text-[var(--tt-text-muted)]">
          Cloud-hosted, full-quality files. {isPremium
            ? <>You&apos;re a <span className="text-[var(--tt-primary)] font-bold">£10 supporter</span> — thank you!</>
            : "Reserved for £10 monthly supporters."}
        </p>
      </div>
      {sub && isPremium && (
        <span className="ml-auto text-xs font-mono-tt bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-3 py-1.5 rounded-full font-bold" data-testid="downloads-quota">
          {sub.downloads_today}/{sub.downloads_cap} today
        </span>
      )}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div
      data-testid="downloads-skeleton-grid"
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
    >
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl aspect-[2/3] tt-skeleton tt-skeleton-poster"
          style={{ animationDelay: `${(i % 5) * 0.12}s` }}
        />
      ))}
    </div>
  );
}

// -- Single poster card -----------------------------------------------------

function DownloadTitleCard({ title, onOpen }) {
  const poster = title.poster_url || (title.poster_path ? TMDB_W500 + title.poster_path : "");
  const label = buildLabel(title);
  return (
    <button
      onClick={onOpen}
      data-testid={`download-card-${title.media_type}-${title.tmdb_id}`}
      className="poster-card relative rounded-xl overflow-hidden group cursor-pointer text-left bg-[var(--tt-surface)] border border-transparent hover:border-[var(--tt-primary)]/40 transition-colors animate-fade-up"
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-[#0c0c0c]">
        {poster ? (
          <img
            src={poster}
            alt={title.title}
            loading="lazy"
            className="poster-img w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--tt-text-faint)]">
            <ImageIcon className="w-10 h-10" />
          </div>
        )}

        {title.year && (
          <div className="absolute top-3 left-3 bg-[var(--tt-primary)] text-white text-[11px] font-bold px-2.5 py-1 rounded shadow-md z-10">
            {title.year}
          </div>
        )}

        {title.vote_average ? (
          <div className="absolute top-3 right-3 bg-black/75 backdrop-blur-md text-[#FFD700] text-xs font-bold px-2 py-1 rounded flex items-center gap-1 z-10">
            <Star className="w-3 h-3 fill-[#FFD700]" strokeWidth={0} />
            <span>{Number(title.vote_average).toFixed(1)}</span>
          </div>
        ) : null}

        {/* Cloud-files badge bottom-left */}
        <div className="absolute bottom-3 left-3 z-10 bg-black/80 backdrop-blur-md text-[10px] font-mono-tt font-bold px-2 py-1 rounded flex items-center gap-1.5 text-emerald-300 border border-emerald-400/30">
          {title.media_type === "tv" ? <Tv className="w-3 h-3" /> : <Film className="w-3 h-3" />}
          {label}
        </div>

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="bg-[var(--tt-primary)] text-white text-xs font-bold px-3 py-2 rounded-full inline-flex items-center gap-2 shadow-lg">
            <Download className="w-3.5 h-3.5" />
            {title.media_type === "tv" ? "Pick episode" : "Download"}
          </span>
        </div>
      </div>
      <div className="px-2 py-3">
        <div className="text-sm font-semibold truncate group-hover:text-[var(--tt-primary)] transition-colors">
          {title.title}
        </div>
      </div>
    </button>
  );
}

// -- Per-title modal --------------------------------------------------------

function TitleDownloadModal({ title, onClose, onAfterDownload }) {
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [openSeason, setOpenSeason] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    if (!title) { setData(null); setDetails(null); return; }
    setLoading(true);
    setDetails(null);
    // Files + TMDB metadata (trailer + screenshots) load in parallel
    Promise.all([
      api.get(`/downloads/title/${title.media_type}/${title.tmdb_id}/files`),
      api.get(`/details/${title.media_type}/${title.tmdb_id}`).catch(() => null),
    ])
      .then(([filesRes, detailsRes]) => {
        setData(filesRes.data);
        if (detailsRes) setDetails(detailsRes.data);
        const keys = Object.keys(filesRes.data.seasons || {});
        setOpenSeason(keys.length === 1 ? keys[0] : keys[0] || null);
      })
      .catch(() => toast.error("Couldn't load this title's files"))
      .finally(() => setLoading(false));
  }, [title]);

  // Close on ESC
  useEffect(() => {
    if (!title) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [title, onClose]);

  if (!title) return null;

  const doDownload = async (fileId, displayName) => {
    setDownloading((s) => ({ ...s, [fileId]: true }));
    try {
      const { data } = await api.post("/downloads/link", { file_id: fileId });
      toast.success(`${displayName || data.title || "Download"} ready · ${data.downloads_today}/${data.downloads_cap} today`);
      window.open(data.url, "_blank", "noopener,noreferrer");
      onAfterDownload?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.startsWith("daily_cap_reached")) {
        toast.error("You've hit today's download cap. Resets at midnight UTC.");
      } else if (e.response?.status === 403) {
        toast.error("Premium donors only.");
      } else {
        toast.error(detail || "Download failed");
      }
    } finally {
      setDownloading((s) => ({ ...s, [fileId]: false }));
    }
  };

  const seasonKeys = data ? Object.keys(data.seasons || {}).sort(
    (a, b) => Number(a) - Number(b),
  ) : [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="download-modal-overlay"
    >
      <div
        className="relative bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="download-modal"
      >
        {/* Hero */}
        <div className="relative">
          <div className="aspect-[16/9] sm:aspect-[16/7] w-full bg-black overflow-hidden">
            {title.backdrop_url && (
              <img
                src={title.backdrop_url}
                alt=""
                className="w-full h-full object-cover opacity-60"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--tt-surface)] via-[var(--tt-surface)]/40 to-transparent" />
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 hover:bg-black flex items-center justify-center"
            data-testid="download-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 px-5 pb-4 flex gap-3 items-end">
            {title.poster_url && (
              <img
                src={title.poster_url}
                alt=""
                className="w-20 sm:w-24 h-auto rounded-lg shadow-2xl border border-white/10 flex-none"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest font-mono-tt text-[var(--tt-primary)] mb-1">
                {title.media_type === "tv" ? "TV Series" : "Movie"} · Cloud Direct Download
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-extrabold leading-tight truncate">
                {title.title} <span className="text-[var(--tt-text-muted)] font-normal">({title.year || "—"})</span>
              </h2>
              <div className="flex items-center gap-3 text-[11px] text-[var(--tt-text-muted)] mt-1 font-mono-tt">
                {title.vote_average ? (
                  <span className="inline-flex items-center gap-1 text-[#FFD700]">
                    <Star className="w-3 h-3 fill-[#FFD700]" strokeWidth={0} />
                    {Number(title.vote_average).toFixed(1)}
                  </span>
                ) : null}
                <span>{title.file_count} file{title.file_count === 1 ? "" : "s"}</span>
                {title.total_size ? <span>{humanSize(title.total_size)}</span> : null}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4">
          {title.overview && (
            <p className="text-sm text-[var(--tt-text-muted)] leading-relaxed mb-4 line-clamp-3 sm:line-clamp-none">
              {title.overview}
            </p>
          )}

          {/* Trailer + screenshots (lazy-loaded from TMDB) */}
          {(details?.trailer_key || (details?.screenshots?.length > 0)) && (
            <div className="grid md:grid-cols-[1.4fr_1fr] gap-3 mb-5" data-testid="download-modal-media">
              {details?.trailer_key && (
                <div data-testid="download-modal-trailer">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-primary)] mb-2">
                    Trailer
                  </div>
                  <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-[var(--tt-border)] bg-black">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${details.trailer_key}?modestbranding=1&rel=0`}
                      title="Trailer"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                </div>
              )}
              {details?.screenshots?.length > 0 && (
                <div data-testid="download-modal-screenshots">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-yellow)] mb-2">
                    Screenshots
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {details.screenshots.slice(0, 6).map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightboxIdx(i)}
                        data-testid={`download-modal-screenshot-${i}`}
                        className="block aspect-video rounded-md overflow-hidden border border-[var(--tt-border)] hover:border-[var(--tt-primary)]/60 transition-colors"
                      >
                        <img src={s} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {loading || !data ? (
            <div className="py-10 flex items-center justify-center text-[var(--tt-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading files…
            </div>
          ) : title.media_type === "tv" ? (
            <div>
              {/* Season dropdown */}
              <div className="flex items-center justify-between gap-3 mb-3 px-1">
                <label htmlFor="download-modal-season-select" className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-primary)]">
                  Season
                </label>
                <select
                  id="download-modal-season-select"
                  value={openSeason ?? ""}
                  onChange={(e) => setOpenSeason(e.target.value)}
                  data-testid="download-modal-season-select"
                  className="flex-1 max-w-[260px] bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm font-semibold outline-none focus:border-[var(--tt-primary)]"
                >
                  {seasonKeys.map((k) => (
                    <option key={k} value={k}>
                      {Number(k) === 0 ? "Specials" : `Season ${k}`}
                      {" · "}
                      {data.seasons[k].length} ep{data.seasons[k].length === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
              </div>
              {/* Episodes for the selected season */}
              {openSeason != null && data.seasons[openSeason] && (
                <ul
                  className="divide-y divide-[var(--tt-border)] rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface-2)] overflow-hidden"
                  data-testid={`download-modal-season-list-${openSeason}`}
                >
                  {data.seasons[openSeason].map((f) => (
                    <FileRow
                      key={f.file_id}
                      file={f}
                      mediaType="tv"
                      busy={!!downloading[f.file_id]}
                      onDownload={() => doDownload(f.file_id, `S${f.season}E${f.episode}`)}
                    />
                  ))}
                </ul>
              )}
            </div>
          ) : (
            // Movies: flat list of available versions
            <ul className="divide-y divide-[var(--tt-border)] rounded-xl border border-[var(--tt-border)] bg-[var(--tt-surface-2)] overflow-hidden">
              {(data.seasons[0] || data.seasons[Object.keys(data.seasons)[0]] || []).map((f) => (
                <FileRow
                  key={f.file_id}
                  file={f}
                  mediaType="movie"
                  busy={!!downloading[f.file_id]}
                  onDownload={() => doDownload(f.file_id, title.title)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Screenshot lightbox */}
      {lightboxIdx >= 0 && details?.screenshots?.length > 0 && (
        <Lightbox
          images={details.screenshots}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onIndex={setLightboxIdx}
        />
      )}
    </div>
  );
}

function FileRow({ file, mediaType, busy, onDownload }) {
  const ep = mediaType === "tv" && file.episode != null
    ? `S${String(file.season).padStart(2, "0")}E${String(file.episode).padStart(2, "0")}`
    : null;
  const [info, setInfo] = useState(file.media_info || null);
  const [probing, setProbing] = useState(false);

  const probe = async () => {
    if (info || probing) return;
    setProbing(true);
    try {
      const { data } = await api.post(`/downloads/probe/${file.file_id}`, null, { timeout: 60000 });
      setInfo(data.media_info);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read media info");
    } finally {
      setProbing(false);
    }
  };

  return (
    <li className="px-4 py-3" data-testid={`download-file-${file.file_id}`}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate" title={file.name}>
            {ep ? <span className="font-mono-tt text-[var(--tt-primary)] mr-2">{ep}</span> : null}
            {file.name}
          </div>
          <div className="text-[10px] font-mono-tt text-[var(--tt-text-muted)] mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <HardDrive className="w-3 h-3" />
              {humanSize(file.size)}
            </span>
            {file.quality && <span>{String(file.quality).toUpperCase()}</span>}
            {file.codec && <span>{String(file.codec).toUpperCase()}</span>}
            {!info && (
              <button
                type="button"
                onClick={probe}
                disabled={probing}
                data-testid={`probe-btn-${file.file_id}`}
                className="inline-flex items-center gap-1 text-[var(--tt-primary)] hover:underline disabled:opacity-50"
              >
                {probing
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Reading…</>
                  : <><Info className="w-3 h-3" /> Read media info</>}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={onDownload}
          disabled={busy}
          data-testid={`download-file-btn-${file.file_id}`}
          className="flex-none inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold disabled:opacity-40"
        >
          {busy
            ? <Loader2 className="w-3 h-3 animate-spin" />
            : <Download className="w-3 h-3" />}
          Download
        </button>
      </div>
      {info && <MediaInfoChips info={info} fileId={file.file_id} />}
    </li>
  );
}

function MediaInfoChips({ info, fileId }) {
  const v = info.video?.[0];
  const chips = [];
  if (info.duration_label) chips.push({ key: "dur", icon: Clock, label: info.duration_label });
  if (v?.resolution) chips.push({ key: "res", icon: Monitor, label: v.resolution + (v.fps ? ` · ${Math.round(v.fps)}fps` : "") });
  if (v?.codec) chips.push({ key: "vcodec", icon: Film, label: String(v.codec).toUpperCase() + (v.bit_depth && v.bit_depth > 8 ? ` ${v.bit_depth}-bit` : "") });
  if (v?.hdr) chips.push({ key: "hdr", icon: Sparkles, label: "HDR", tone: "yellow" });

  const audioCount = info.audio?.length || 0;
  if (audioCount > 0) {
    const a = info.audio[0];
    const ch = a.channels === 2 ? "2.0" : a.channels === 6 ? "5.1" : a.channels === 8 ? "7.1" : `${a.channels}ch`;
    chips.push({
      key: "audio",
      icon: Volume2,
      label: `${String(a.codec).toUpperCase()} ${ch}${audioCount > 1 ? ` · ${audioCount} tracks` : ""}`,
    });
  }
  const subCount = info.subtitles?.length || 0;
  if (subCount > 0) {
    chips.push({ key: "subs", icon: Subtitles, label: `${subCount} subtitle${subCount === 1 ? "" : "s"}` });
  }
  if (info.bitrate) {
    chips.push({ key: "br", icon: Activity, label: `${Math.round(info.bitrate / 1000)} kbps` });
  }

  return (
    <div
      data-testid={`media-info-chips-${fileId}`}
      className="mt-2 flex items-center gap-1.5 flex-wrap"
    >
      {chips.map((c) => (
        <span
          key={c.key}
          className={`inline-flex items-center gap-1 text-[10px] font-mono-tt font-bold px-2 py-0.5 rounded-full border ${c.tone === "yellow"
            ? "border-[var(--tt-yellow)]/40 bg-[var(--tt-yellow)]/10 text-[var(--tt-yellow)]"
            : "border-[var(--tt-border)] bg-black/40 text-white/85"}`}
        >
          <c.icon className="w-3 h-3" />
          {c.label}
        </span>
      ))}
    </div>
  );
}

// -- Paywall (unchanged) ----------------------------------------------------

// -- Quality chip strip -----------------------------------------------------

const QUALITY_OPTIONS = [
  { value: "",      label: "All" },
  { value: "720p",  label: "720p" },
  { value: "1080p", label: "1080p" },
  { value: "2160p", label: "4K" },
];

function QualityChips({ quality, hdrOnly, onQuality, onHdr }) {
  return (
    <div
      className="flex items-center justify-center gap-1.5 mb-5 flex-wrap"
      data-testid="quality-chips"
    >
      {QUALITY_OPTIONS.map((opt) => {
        const active = quality === opt.value;
        return (
          <button
            key={opt.value || "all"}
            onClick={() => onQuality(opt.value)}
            data-testid={`quality-chip-${opt.value || "all"}`}
            className={`text-[11px] font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full border transition-colors ${active
              ? "bg-[var(--tt-primary)] border-[var(--tt-primary)] text-white shadow-[0_0_12px_-2px_rgba(255,42,109,0.55)]"
              : "bg-transparent border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-white/40"}`}
          >
            {opt.label}
          </button>
        );
      })}
      <span className="mx-1 h-4 w-px bg-[var(--tt-border)]" aria-hidden />
      <button
        onClick={() => onHdr(!hdrOnly)}
        data-testid="quality-chip-hdr"
        className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full border transition-colors ${hdrOnly
          ? "bg-[var(--tt-yellow)] border-[var(--tt-yellow)] text-black shadow-[0_0_12px_-2px_rgba(255,215,0,0.6)]"
          : "bg-transparent border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-[var(--tt-yellow)] hover:border-[var(--tt-yellow)]/40"}`}
      >
        <Sparkles className="w-3 h-3" />
        HDR only
      </button>
    </div>
  );
}


function LockedHero({ tier }) {
  return (
    <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-8 max-w-3xl mx-auto" data-testid="downloads-locked">
      <Lock className="w-10 h-10 text-[var(--tt-primary)] mb-4" />
      <h2 className="font-display text-2xl font-extrabold mb-2">Downloads are a £10 supporter perk</h2>
      <p className="text-sm text-[var(--tt-text-muted)] mb-6 max-w-xl">
        TorHub is built and hosted by one person. Supporters who chip in £10/month unlock direct
        downloads of the entire cloud library at full quality, capped at 8 downloads/day.
        £5 supporters still get the gorgeous Fire TV streaming app — pick whatever feels fair.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <PlanCard
          name="TorHub TV"
          price="£5"
          icon={Tv}
          features={["Fire TV / Android TV app", "Unlimited streaming", "Continue Watching", "Weekly digest"]}
          cta="Subscribe £5/mo"
          packageId="tv"
          current={tier === "tv"}
        />
        <PlanCard
          name="TorHub TV + Downloads"
          price="£10"
          icon={Crown}
          features={["Everything in £5 tier", "Direct downloads", "8 downloads / day", "Priority requests"]}
          cta="Subscribe £10/mo"
          packageId="premium"
          highlight
          current={tier === "premium"}
        />
      </div>
      <p className="text-[11px] text-[var(--tt-text-faint)] mt-5">
        Subscriptions are charged monthly via Stripe. Cancel anytime from your card statement or message the admin.
      </p>
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-[var(--tt-primary)] mt-4 hover:underline">
        <Heart className="w-3.5 h-3.5" /> Back to browsing
      </Link>
    </div>
  );
}

function PlanCard({ name, price, icon: Icon, features, cta, highlight, current, packageId }) {
  const [busy, setBusy] = useState(false);
  const subscribe = async () => {
    if (!packageId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/stripe/checkout",
        { package_id: packageId, origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout init failed");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout error");
      setBusy(false);
    }
  };
  return (
    <div className={`rounded-2xl border p-5 ${highlight
      ? "bg-[var(--tt-primary)]/10 border-[var(--tt-primary)]/40"
      : "bg-[var(--tt-surface-2)] border-[var(--tt-border)]"}`}
      data-testid={`plan-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${highlight ? "text-[var(--tt-primary)]" : "text-[var(--tt-yellow)]"}`} />
        <h3 className="font-display font-extrabold text-lg">{name}</h3>
        {current && <span className="text-[9px] uppercase font-bold bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded ml-auto">Current</span>}
      </div>
      <div className="font-display text-3xl font-extrabold mb-3">{price}<span className="text-sm text-[var(--tt-text-muted)] font-normal">/mo</span></div>
      <ul className="space-y-1.5 text-xs text-[var(--tt-text-muted)] mb-4">
        {features.map((f) => <li key={f}>· {f}</li>)}
      </ul>
      <button
        onClick={subscribe}
        disabled={busy || current}
        data-testid={`plan-subscribe-${packageId}`}
        className={`w-full py-2 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${highlight
          ? "bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white"
          : "bg-white text-black hover:bg-white/90"}`}
      >
        {busy ? "Opening Stripe…" : current ? "Active" : cta}
      </button>
    </div>
  );
}


// -- Pagination control -----------------------------------------------------

function Pagination({ page, totalPages, onChange }) {
  // Compact paginator: ⇤ ◀ … 1 2 [3] 4 5 … ▶ ⇥
  const windowSize = 2; // pages either side of current
  const pages = [];
  const push = (p) => { if (!pages.includes(p)) pages.push(p); };
  push(1);
  for (let i = page - windowSize; i <= page + windowSize; i++) {
    if (i > 1 && i < totalPages) push(i);
  }
  if (totalPages > 1) push(totalPages);
  pages.sort((a, b) => a - b);
  // Insert gaps
  const withGaps = [];
  pages.forEach((p, idx) => {
    if (idx > 0 && p - pages[idx - 1] > 1) withGaps.push("…");
    withGaps.push(p);
  });

  const btn = (label, target, disabled, testid, extra = "") =>
    <button
      key={testid}
      onClick={() => onChange(target)}
      disabled={disabled}
      data-testid={testid}
      className={`min-w-[40px] h-9 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${extra}`}
    >{label}</button>;

  return (
    <div
      data-testid="downloads-pagination"
      className="flex items-center justify-center flex-wrap gap-1.5 mt-10"
    >
      {btn("⇤", 1, page <= 1, "downloads-page-first", "bg-[var(--tt-surface-2)] hover:bg-[#222]")}
      {btn("◀ Prev", Math.max(1, page - 1), page <= 1, "downloads-page-prev", "bg-[var(--tt-surface-2)] hover:bg-[#222]")}
      {withGaps.map((p, i) => {
        if (p === "…") return <span key={`g-${i}`} className="px-1 text-[var(--tt-text-muted)]">…</span>;
        const active = p === page;
        return (
          <button
            key={p}
            onClick={() => onChange(p)}
            data-testid={`downloads-page-${p}`}
            aria-current={active ? "page" : undefined}
            className={`min-w-[40px] h-9 px-3 rounded-lg text-xs font-semibold transition-colors ${active
              ? "bg-[var(--tt-primary)] text-white shadow-[0_0_14px_-2px_rgba(255,42,109,0.55)]"
              : "bg-[var(--tt-surface-2)] hover:bg-[#222] text-white"}`}
          >{p}</button>
        );
      })}
      {btn("Next ▶", Math.min(totalPages, page + 1), page >= totalPages, "downloads-page-next", "bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white")}
      {btn("⇥", totalPages, page >= totalPages, "downloads-page-last", "bg-[var(--tt-surface-2)] hover:bg-[#222]")}
    </div>
  );
}
